import React, { useState, useEffect } from "react";
import { Button, Input, Select, DatePicker, Timeline, Tag, Divider, Row, Col } from "antd";
import { SaveOutlined, ShoppingCartOutlined, SendOutlined, CheckCircleOutlined, ShopOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { updateOrderShippingStatus, getOrderTrackingInfo } from "../../../../api/labiq/LabOrdersApi";
import { LabOrder, LabOrderShippingStatusOptions, DeliveryVendorOptions } from "../../../../types/lab/LabOrderTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import { DetailDrawer, SectionCard, StatusTag } from "../../../../components/shared";

interface ShippingTrackingProps {
	isOpen: boolean;
	onClose: () => void;
	order: LabOrder;
	onOrderUpdate: () => void;
}

interface TrackingInfo {
	order_id: number;
	order_number: string;
	shipping_status: string;
	tracking_number: string;
	delivery_vendor: string;
	shipped_at: string;
	delivered_at: string;
	shipping_address: string;
	shipping_notes: string;
}

const ShippingTrackingComponent: React.FC<ShippingTrackingProps> = ({ isOpen, onClose, order, onOrderUpdate }) => {
	const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | null>(null);
	const [shippingData, setShippingData] = useState({
		shipping_status: "",
		tracking_number: "",
		delivery_vendor: "",
		shipped_at: null as dayjs.Dayjs | null,
		delivered_at: null as dayjs.Dayjs | null,
		shipping_notes: "",
	});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		if (isOpen && order) {
			loadTrackingInfo();
		}
	}, [isOpen, order]);

	const loadTrackingInfo = async () => {
		setLoading(true);
		try {
			const info = await getOrderTrackingInfo(order.id);
			setTrackingInfo(info);
			setShippingData({
				shipping_status: info.shipping_status || "",
				tracking_number: info.tracking_number || "",
				delivery_vendor: info.delivery_vendor || "",
				shipped_at: info.shipped_at ? dayjs(info.shipped_at) : null,
				delivered_at: info.delivered_at ? dayjs(info.delivered_at) : null,
				shipping_notes: info.shipping_notes || "",
			});
		} catch (error) {
			showError("Failed to load tracking information");
			console.error("Error loading tracking info:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			const updateData = {
				...shippingData,
				shipped_at: shippingData.shipped_at?.toISOString(),
				delivered_at: shippingData.delivered_at?.toISOString(),
			};

			await updateOrderShippingStatus(order.id, updateData);
			showSuccess("Shipping status updated successfully");
			onOrderUpdate();
			onClose();
		} catch (error) {
			showError("Failed to update shipping status");
			console.error("Error updating shipping:", error);
		} finally {
			setSaving(false);
		}
	};

	const getTimelineIcon = (type: string) => {
		switch (type) {
			case "order":
				return <ShoppingCartOutlined />;
			case "shipped":
				return <SendOutlined />;
			case "delivered":
				return <CheckCircleOutlined />;
			case "received":
				return <ShopOutlined />;
			default:
				return <CheckCircleOutlined />;
		}
	};

	const getTimelineColor = (type: string): string => {
		switch (type) {
			case "order":
				return "#9C27B0";
			case "shipped":
				return "#FF9800";
			case "delivered":
				return "#4CAF50";
			case "received":
				return "#2196F3";
			default:
				return "#1890ff";
		}
	};

	const getTimelineEvents = () => {
		const events = [];

		if (order.ordered_at) {
			events.push({
				color: getTimelineColor("order"),
				dot: getTimelineIcon("order"),
				children: (
					<div>
						<div style={{ fontWeight: 500, marginBottom: 4 }}>Order Placed</div>
						<Tag color="purple">{new Date(order.ordered_at).toLocaleDateString()}</Tag>
					</div>
				),
			});
		}

		if (shippingData.shipped_at) {
			events.push({
				color: getTimelineColor("shipped"),
				dot: getTimelineIcon("shipped"),
				children: (
					<div>
						<div style={{ fontWeight: 500, marginBottom: 4 }}>Shipped</div>
						<Tag color="orange">{shippingData.shipped_at.format("DD/MM/YYYY")}</Tag>
						{shippingData.tracking_number && <div style={{ fontSize: "12px", color: "var(--mg-text-secondary)", marginTop: 4 }}>Tracking: {shippingData.tracking_number}</div>}
					</div>
				),
			});
		}

		if (shippingData.delivered_at) {
			events.push({
				color: getTimelineColor("delivered"),
				dot: getTimelineIcon("delivered"),
				children: (
					<div>
						<div style={{ fontWeight: 500, marginBottom: 4 }}>Delivered</div>
						<Tag color="green">{shippingData.delivered_at.format("DD/MM/YYYY")}</Tag>
					</div>
				),
			});
		}

		if (order.received_at) {
			events.push({
				color: getTimelineColor("received"),
				dot: getTimelineIcon("received"),
				children: (
					<div>
						<div style={{ fontWeight: 500, marginBottom: 4 }}>Received at Lab</div>
						<Tag color="blue">{new Date(order.received_at).toLocaleDateString()}</Tag>
					</div>
				),
			});
		}

		return events;
	};

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			title={`Shipping Tracking – Order ${order.order_number}`}
			width="70vw"
			loading={loading}
			headerActions={
				<Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} size="small">
					Save
				</Button>
			}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				{/* Current Status */}
				<SectionCard title="Current Status">
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div>
							<StatusTag status={shippingData.shipping_status || "not_set"} label={shippingData.shipping_status ? undefined : "Not Set"} />
						</div>
						{shippingData.tracking_number && (
							<div style={{ textAlign: "right" }}>
								<div style={{ fontSize: "12px", color: "var(--mg-text-secondary)" }}>Tracking Number</div>
								<div style={{ fontSize: "16px", fontWeight: 600, marginTop: 4 }}>{shippingData.tracking_number}</div>
								<div style={{ fontSize: "12px", color: "var(--mg-text-secondary)", marginTop: 2 }}>{shippingData.delivery_vendor}</div>
							</div>
						)}
					</div>
				</SectionCard>

				{/* Timeline */}
				{getTimelineEvents().length > 0 && (
					<SectionCard title="Shipping Timeline">
						<Timeline items={getTimelineEvents()} />
					</SectionCard>
				)}

				<Divider style={{ margin: "8px 0" }} />

				{/* Update Form */}
				<SectionCard title="Update Shipping Status">
					<Row gutter={[12, 12]}>
						<Col xs={24} sm={12}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Shipping Status</label>
							<Select
								value={shippingData.shipping_status}
								options={LabOrderShippingStatusOptions}
								onChange={(value) => setShippingData((prev) => ({ ...prev, shipping_status: value }))}
								placeholder="Select shipping status"
								size="small"
								style={{ width: "100%" }}
							/>
						</Col>

						<Col xs={24} sm={12}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Delivery Vendor</label>
							<Select
								value={shippingData.delivery_vendor}
								options={DeliveryVendorOptions}
								onChange={(value) => setShippingData((prev) => ({ ...prev, delivery_vendor: value }))}
								placeholder="Select delivery vendor"
								size="small"
								style={{ width: "100%" }}
							/>
						</Col>

						<Col xs={24}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Tracking Number</label>
							<Input value={shippingData.tracking_number} onChange={(e) => setShippingData((prev) => ({ ...prev, tracking_number: e.target.value }))} placeholder="Enter tracking number" size="small" />
						</Col>

						<Col xs={24} sm={12}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Shipped Date</label>
							<DatePicker
								value={shippingData.shipped_at}
								onChange={(date) => setShippingData((prev) => ({ ...prev, shipped_at: date }))}
								showTime={{ format: "HH:mm" }}
								format="DD/MM/YYYY HH:mm"
								size="small"
								style={{ width: "100%" }}
							/>
						</Col>

						<Col xs={24} sm={12}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Delivered Date</label>
							<DatePicker
								value={shippingData.delivered_at}
								onChange={(date) => setShippingData((prev) => ({ ...prev, delivered_at: date }))}
								showTime={{ format: "HH:mm" }}
								format="DD/MM/YYYY HH:mm"
								size="small"
								style={{ width: "100%" }}
							/>
						</Col>

						<Col xs={24}>
							<label style={{ display: "block", fontSize: "12px", marginBottom: 4, fontWeight: 500 }}>Shipping Notes</label>
							<Input.TextArea value={shippingData.shipping_notes} onChange={(e) => setShippingData((prev) => ({ ...prev, shipping_notes: e.target.value }))} placeholder="Enter shipping notes" rows={3} size="small" />
						</Col>
					</Row>
				</SectionCard>
			</div>
		</DetailDrawer>
	);
};

export default ShippingTrackingComponent;
